// Floating palette picker. Summoned by holding the right-controller A
// button — appears in front of the user's gaze, hides on release. Aim
// the laser at a swatch and pull the trigger to set the active palette
// index for DRAW.
//
// Pure presentation: paintPalettePanel writes the canvas based on
// caller-supplied state (active + hot swatch indices). pickSwatch maps
// a UV coordinate on the mesh to a swatch index. Hit testing is done
// against the mesh by THREE.Raycaster in main.ts; this module just
// decodes the UV.

import * as THREE from "three";
import { paintGlassPanel, roundRectPath, setupPanelTexture } from "./glass";
import { PALETTE_SIZE, paletteColorCss } from "./palette";

const COLS = 4;
// Rows grow with the palette so every swatch lays out (16 → 4 rows, 18 → 5).
const ROWS = Math.ceil(PALETTE_SIZE / COLS);
const CANVAS_W = 1024;
const CANVAS_H = 1024;
const MARGIN = 32;
const GAP = 16;

// Panel mesh size in world meters. 30cm × 30cm reads as a comfortable
// hand-held picker at the 0.8m summon distance.
const PANEL_W = 0.3;
const PANEL_H = 0.3;

const canvas = document.createElement("canvas");
canvas.width = CANVAS_W;
canvas.height = CANVAS_H;
const texture = new THREE.CanvasTexture(canvas);
setupPanelTexture(texture);

const material = new THREE.MeshBasicMaterial({
  map: texture,
  transparent: true,
  side: THREE.DoubleSide,
  // depthTest off so the panel always lands on top of the cube and any
  // voxel geometry it might overlap. Matches the cursor and tool
  // overlays' "UI always wins" convention.
  depthTest: false,
  depthWrite: false,
});

export const palettePanelMesh = new THREE.Mesh(
  new THREE.PlaneGeometry(PANEL_W, PANEL_H),
  material,
);
palettePanelMesh.renderOrder = 950;
palettePanelMesh.visible = false;

interface SwatchRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function swatchRect(idx: number): SwatchRect {
  // idx is 1-based (1..PALETTE_SIZE). Row-major layout, top-left = 1.
  const i = idx - 1;
  const col = i % COLS;
  const row = Math.floor(i / COLS);
  const w = (CANVAS_W - 2 * MARGIN - (COLS - 1) * GAP) / COLS;
  const h = (CANVAS_H - 2 * MARGIN - (ROWS - 1) * GAP) / ROWS;
  return {
    x: MARGIN + col * (w + GAP),
    y: MARGIN + row * (h + GAP),
    w,
    h,
  };
}

// Repaint the canvas with the current state. `hotIndex` is the swatch
// currently under any controller's laser (null if none).
export function paintPalettePanel(
  activeIndex: number,
  hotIndex: number | null,
): void {
  const ctx = canvas.getContext("2d")!;
  paintGlassPanel(ctx, CANVAS_W, CANVAS_H);

  const SW_RADIUS = 28;
  for (let idx = 1; idx <= PALETTE_SIZE; idx++) {
    const r = swatchRect(idx);
    // Rounded color swatch with a faint inner edge so light colors still
    // read against the glass.
    roundRectPath(ctx, r.x, r.y, r.w, r.h, SW_RADIUS);
    ctx.fillStyle = paletteColorCss(idx);
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(0, 0, 0, 0.20)";
    ctx.stroke();

    // Active = bright outer ring; hot = thinner, dimmer ring. Active wins
    // if the laser is on the already-selected swatch.
    if (idx === activeIndex) {
      roundRectPath(ctx, r.x - 6, r.y - 6, r.w + 12, r.h + 12, SW_RADIUS + 6);
      ctx.lineWidth = 8;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
      ctx.stroke();
    } else if (idx === hotIndex) {
      roundRectPath(ctx, r.x - 3, r.y - 3, r.w + 6, r.h + 6, SW_RADIUS + 3);
      ctx.lineWidth = 4;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.70)";
      ctx.stroke();
    }
  }

  texture.needsUpdate = true;
}

// Decode a UV hit on the panel mesh to a swatch index, or null if the
// hit lands in a margin/gap.
export function pickSwatch(uvX: number, uvY: number): number | null {
  const x = uvX * CANVAS_W;
  // Canvas Y grows downward; UV Y grows upward — flip.
  const y = (1 - uvY) * CANVAS_H;
  for (let idx = 1; idx <= PALETTE_SIZE; idx++) {
    const r = swatchRect(idx);
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
      return idx;
    }
  }
  return null;
}
